import React, { useEffect, useState, useRef } from "react";
import Stack from "components/common/Stack";
import { Table, Typography, Button } from "antd";
import { Link } from "react-router-dom";
import displayErrorNotification from "utils/displayErrorNotification";
import sorters from "utils/sorters";
import Toolbar from "components/common/Toolbar";
import { getIndustryExperienceReport, getReportById } from "api/reportsApi";
import IndustryExperienceReportFilters, {
  filterIndustryExperienceReport,
} from "./IndustryExperienceReportFilters";
import { Parser } from "json2csv";
import moment from "moment";
import ButtonRow from "components/common/ButtonRow";
import useLocalStorage from "utils/useLocalStorage";
import { defaultIndustryExperienceReportFilterSettings } from "types/IndustryExperienceReportPreferences";
import SavePreferencesButton from "components/common/SavePreferencesButton";
import { convertIndustryExperience } from "utils/convert";

const IndustryExperienceReportList = (props) => {
  const [report, setReport] = useState([]);
  const [responses, setResponses] = useState([]);
  const [filteredResponses, setFilteredResponses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterSettings, setFilterSettings] = useLocalStorage(
    "IndustryExperienceReport_FilterSettings",
    defaultIndustryExperienceReportFilterSettings()
  );
  const [sortSettings, setSortSettings] = useLocalStorage(
    "IndustryExperienceReport_SortSettings",
    {
      key: "name",
      order: "ascend",
    }
  );
  const searchRef = useRef();

  useEffect(() => {
    setLoading(true);
    getIndustryExperienceReport()
      .then((result) => {
        const converted = convertIndustryExperience(result);
        setResponses(converted);
        setFilteredResponses(converted);
        filterIndustryExperienceReport(
          {
            onSearch: setFilteredResponses,
            responses: converted,
            setSettings: setFilterSettings,
          },
          {},
          {
            searchTerm: filterSettings.search,
            selectedPractices: filterSettings.practice,
            selectedPositions: filterSettings.position,
            selectedOffices: filterSettings.office,
            selectedIndustryExperience: filterSettings.industryExperience,
          }
        );
      })
      .catch(displayErrorNotification)
      .then(() => {
        setLoading(false);
        if (
          searchRef.current !== undefined &&
          searchRef.current.input !== null
        ) {
          searchRef.current.focus();
        }
      });
  }, []);

  useEffect(() => {
    setReport(getReportById(4));
  }, []);

  let columns = [
    {
      title: "Name",
      key: "name",
      type: sorters.TYPE_STRING,
      render: (record) => (
        <Link to={`/employees/${record.id}`}>{record.name}</Link>
      ),
      sorter: (a, b) => sorters.string(a.name, b.name),
    },
    {
      title: "Practice",
      dataIndex: "practice",
      key: "practice",
      type: sorters.TYPE_STRING,
      sorter: (a, b) => sorters.string(a.practice, b.practice),
    },
    {
      title: "Major",
      dataIndex: "major",
      key: "major",
      type: sorters.TYPE_STRING,
      sorter: (a, b) => sorters.string(a.major, b.major),
    },
    {
      title: "Position",
      dataIndex: "position",
      key: "position",
      type: sorters.TYPE_STRING,
      sorter: (a, b) => sorters.string(a.position, b.position),
    },
    {
      title: "Location",
      dataIndex: "office",
      key: "office",
      type: sorters.TYPE_STRING,
      sorter: (a, b) => sorters.string(a.office, b.office),
    },
    {
      title: "Email Address",
      key: "email",
      type: sorters.TYPE_STRING,
      render: (r) => <a href={`mailto:${r.email}`}>{r.email}</a>,
      sorter: (a, b) => sorters.string(a.email, b.email),
    },
    {
      title: "Industry Experience",
      dataIndex: "industryExperience",
      key: "industryExperience",
      type: sorters.TYPE_STRING,
      sorter: (a, b) =>
        sorters.string(a.industryExperience, b.industryExperience),
    },
  ];

  const columnsSortSettings = columns.map((column) => {
    if (column.key === sortSettings.key) {
      return { ...column, defaultSortOrder: sortSettings.order };
    }
    return column;
  });
  columns = columnsSortSettings;

  function onChange(_pagination, _filters, sorter) {
    if (Object.keys(sorter).length > 0) {
      setSortSettings({
        key: sorter.columnKey,
        order: sorter.order,
      });
    }
  }

  const exportEmployeeIndustryExperience = () => {
    const column = columns.find((c) => c.defaultSortOrder !== undefined);
    let columnName = "name";
    let sortOrder = true; // ascend
    let type = sorters.TYPE_STRING;
    if (column !== undefined) {
      columnName = column.key;
      sortOrder = column.defaultSortOrder === "ascend";
      type = column.type;
    }
    const exportable = filteredResponses
      .sort((a, b) => sorters.multi(a, b, columnName, type, sortOrder))
      .map((k) => {
        return {
          Name: k.name,
          Email: k.email,
          Practice: k.practice,
          "Major": k.major,
          Position: k.position,
          Location: k.office,
          "Industry Experience": k.industryExperience,
        };
      });

    const json2CsvParser = new Parser();
    const csv = json2CsvParser.parse(exportable);
    const blob = new Blob([csv], { type: "text/csv", encoding: "UTF-8" });
    const link = document.createElement("a");
    link.href = window.URL.createObjectURL(blob);
    link.download = `EmployeeIndustryExperience-${moment().format(
      "YYYYMMDD-HHmmss"
    )}.csv`;
    link.click();
  };

  return (
    <Stack>
      <ButtonRow style={{ float: "right" }}>
        {!loading && (
          <Button icon="download" onClick={exportEmployeeIndustryExperience}>
            Export
          </Button>
        )}
        <SavePreferencesButton
          keys={[
            "IndustryExperienceReport_FilterSettings",
            "IndustryExperienceReport_SortSettings",
          ]}
          disabled={loading}
        />
      </ButtonRow>
      <Typography.Title>{report.name}</Typography.Title>
      <Typography.Title level={3}>{report.description}</Typography.Title>
      <Toolbar>
        <IndustryExperienceReportFilters
          onSearch={setFilteredResponses}
          responses={responses}
          searchRef={searchRef}
          disabled={loading}
          settings={filterSettings}
          setSettings={setFilterSettings}
        />
        <div>
          <span style={{ margin: "0 10px" }}>
            Showing {filteredResponses.length} of {responses.length} Responses
          </span>
        </div>
      </Toolbar>
      <Table
        pagination={{ pageSize: 25, position: "top" }}
        rowKey="id"
        columns={columns}
        dataSource={filteredResponses}
        loading={loading}
        onChange={onChange}
      />
    </Stack>
  );
};

export default IndustryExperienceReportList;
